import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import arrowIcon from "@iconify/icons-mdi/arrow-right";
import checkBold from "@iconify/icons-mdi/check-bold";
import closeThick from "@iconify/icons-mdi/close-thick";
import messageOutline from "@iconify/icons-mdi/message-outline";
import messageText from "@iconify/icons-mdi/message-text";
import pencilIcon from "@iconify/icons-mdi/pencil";
import plusIcon from "@iconify/icons-mdi/plus";
import { InlineIcon } from "@iconify/react";
import type { TFunction } from "i18next";
import {
  MyDocumentsActionCell,
  MyDocumentsDocumentEditorButtons,
  MyDocumentsDocumentVisibility,
} from "my-documents/components";
import ApplicationState from "my-tax/components/application-state";
import useMyTimeMessagesList from "my-time/hooks/use-messages-list/use-messages-list";
import {
  OcConfirmationDialog,
  OcDataTableBodyCellState,
  OcInfoDialog,
} from "oc/components";
import { getFormatBytes, getFormatTextWidthValue } from "oc/utils";
import parsePipedName from "oc/utils/get-parse-pipedName";
import TrueIcon from "@iconify/icons-mdi/check";
import FalseIcon from "@iconify/icons-mdi/close";
import UnlockIcon from "@iconify/icons-mdi/lock-open-variant-outline";
import LockIcon from "@iconify/icons-mdi/lock-outline";

// Types for props
interface ColumnDef {
  id: string;
  type?: string;
  [key: string]: any;
}

interface TableDef {
  id: string;
  columns?: ColumnDef[];
}

interface MyTimeItem {
  myTimeType: string;
}

interface Icons {
  add?: boolean;
  arrow?: boolean;
  edit?: boolean;
}

interface RowType {
  _id: string;
  [key: string]: any;
  messages?: any[];
  messageCount?: number;
  icons?: Icons;
  startTime?: string;
  endTime?: string;
  endDate?: string;
  description?: string;
  size?: number;
  user?: string;
  active?: boolean;
  error?: string;
  type?: string;
  role?: string;
  filename?: string;
  slug?: string;
}

interface Props {
  row: RowType;
  myDocumentsOperator: boolean;
  filteredTableDef: TableDef;
  handleMoreButton: (
    args:
      | RowType
      | ({ action: string; row: RowType } & Record<string, any>)
  ) => void;
  inlineEdit: boolean;
  onEditDocument: (row: RowType) => void;
  removeUpload: (row: RowType) => void;
  isNotMyTimeExaminer: boolean;
  matchingMyTimeitems: MyTimeItem[] | any;
  isMobile: boolean;
}

const ICON_BUTTON_STYLE = {
  border: "1px solid #919699",
  borderRadius: "50%",
  cursor: "pointer",
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#919699",
} as const;

const CENTER_KEYS = [
  "icons",
  "actionButtons",
  "copyButton",
  "documentActionButtons",
  "documentEditorButtons",
  "editButton",
  "myTimeActions",
] as const;

const HIDE_LABEL_KEYS = [
  "copyButton",
  "actionButtons",
  "editButton",
  "commonEditButton",
  "documentActionButtons",
  "documentEditorButtons",
  "myTimeActions",
  "icons",
] as const;

const CURRENCY_KEYS = [
  "amount",
  "valueOfBenefit",
  "summaryCost",
  "cost"
] as const;

const NO_MORE_BUTTON_TABLES: readonly string[] = [
  "my-leave-orphaned-leave-requests-table",
  "my-time-employee-without-examiner-request-table",
  "my-documents-document-list-table-def",
  "my-leave-my-fulfilled-leaves-table-def",
  "my-cafeteria-benefits-table-def",
  "my-cafeteria-archived-benefits-table-def",
  "my-time-dashboard-list-table",
  "my-time-dashboard-list-table-authorization",
  "my-time-dashboard-list-table-examiner-approval-only",
  "my-time-dashboard-list-table-viewer",
  "my-time-employee-admissions-table",
  "my-time-employee-admissions-by-admin-table",
  "my-time-employee-closing-period-request-table",
  "my-time-closing-period-not-approved-table",
  "my-time-closing-period-not-approved-wo-examiner-table",
  "my-time-sheet-my-requests-dashboard-table-desktop",
  "my-time-sheet-my-requests-dasboard-table",
  "my-hr-import-errors-table",
  "my-hr-create-employees-list-table",
  "my-hr-add-mass-roles-errors-table",
  "my-hr-remove-mass-roles-errors-table",
  "my-time-employee-admissions-approval-table",
  "my-sheet-costs-settings-table-def",
  "my-sheet-entyty-settings-table-def",
  "my-time-closing-period-not-authorized-table",
  "mass-upload-history-table-def",
];

const LABEL_OVERRIDES: Record<string, string> = {
  messages: "OC_MESSAGES",
  date: "OC_PERIOD",
  size: "MY_DOCUMENTS_SIZE",
  user: "MY_DOCUMENTS_UPLOADER",
  myTimeType: "OC_TYPE",
  userView: ""
};

function asArray<T>(value: any): T[] {
  return Array.isArray(value) ? value : [];
}

export const OcIsMobileTableCollapse: React.FC<Props> = React.memo(
  ({
    row,
    myDocumentsOperator,
    filteredTableDef,
    handleMoreButton,
    inlineEdit: _inlineEdit,
    onEditDocument,
    removeUpload,
    isNotMyTimeExaminer,
    matchingMyTimeitems,
    isMobile,
  }) => {
    const { t, i18n }: { t: TFunction; i18n: any } = useTranslation();
    const theme: Theme = useTheme();
    const { data: messages } = useMyTimeMessagesList({ request: row._id });
    const rawColumns = filteredTableDef?.columns;
    const columns: ColumnDef[] = asArray<ColumnDef>(rawColumns);
    const hasIndicator = columns.some(c => c.type === "myTimeMessageIndicator");

    const overtimeItems: MyTimeItem[] = asArray<MyTimeItem>(matchingMyTimeitems);
    const hasOvertime = useMemo(
      () => overtimeItems.some(item => item.myTimeType === "OVERTIME"),
      [overtimeItems]
    );

    const baseColumns = useMemo<string[]>(() => {
      const tableId = filteredTableDef.id;
      const ids = columns.map(c => c.id);

      const extras: Record<string, string[]> = {
        "my-documents-document-list-table-def": [
          "documentActionButtons",
          "documentEditorButtons",
        ],
        "my-cafeteria-archived-benefits-table-def": ["copyButton"],
        "my-leave-orphaned-leave-requests-table": ["actionButtons"],
        "my-cafeteria-benefits-table-def": ["editButton"],
        "my-sheet-costs-settings-table-def": ["myTimeActions"],
        "my-sheet-entyty-settings-table-def": ["myTimeActions"],
      };

      const constants = [
        "size",
        "user",
        "date",
        "icons",
        ...(tableId === "my-documents-document-list-table-def" && myDocumentsOperator
          ? []
          : ["userView"]),
      ];

      const specialRemove: Record<string, string[]> = {
        "my-time-employee-admissions-by-admin-table": ["icons"],
        "my-time-sheet-employee-requests-table": ["date"],
      };
      const toRemove = specialRemove[tableId] || [];

      const all = [...ids, ...(extras[tableId] ?? []), ...constants];
      const unique = Array.from(new Set(all));
      return unique.filter(col => !toRemove.includes(col));
    }, [columns, filteredTableDef.id, myDocumentsOperator]);

    const rowForEntries = useMemo(() => {
      let r = { ...row };

      if (filteredTableDef.id === "my-leave-orphaned-leave-requests-table") {
        r.actionButtons = null;
      }

      if (filteredTableDef.id === "my-cafeteria-archived-benefits-table-def") {
        r.copyButton = null;
      }

      if (filteredTableDef.id === "my-cafeteria-benefits-table-def") {
        r.editButton = null;
      }

      if (
        filteredTableDef.id === "my-sheet-entyty-settings-table-def" ||
        filteredTableDef.id === "my-sheet-costs-settings-table-def"
      ) {
        r.myTimeActions = null;
      }

      if (filteredTableDef.id === "my-documents-document-list-table-def") {
        r.documentActionButtons = null;
        if (!myDocumentsOperator) {
          r.documentEditorButtons = null;
        }
      }

      const dashboardTableIds = [
        "my-time-dashboard-list-table",
        "my-time-dashboard-list-table-authorization",
        "my-time-dashboard-list-table-examiner-approval-only",
        "my-time-dashboard-list-table-viewer",
      ];

      if (dashboardTableIds.includes(filteredTableDef.id)) {
        r.approvedAt = row.approvedAt || null;
        r.authorizedAt = row.authorizedAt || null;
        r.viewWorkSchedule = null; // Virtual column
      }

      return r;
    }, [filteredTableDef.id, myDocumentsOperator, row]);

    const finalRow = useMemo(() => ({
      ...rowForEntries,
      messages: Array.isArray(rowForEntries.messages) ? rowForEntries.messages : [],
    }), [rowForEntries]);

    const rowEntries = useMemo<[string, any][]>(() => {
      let entries = baseColumns
        .filter(key => Object.prototype.hasOwnProperty.call(finalRow, key))
        .map(key => [key, (finalRow as any)[key]] as [string, any]);

      if (filteredTableDef.id === "my-documents-document-list-table-def") {
        const docEntries = entries.filter(
          ([key]) => key === "documentActionButtons" || key === "documentEditorButtons"
        );
        entries = entries.filter(
          ([key]) => key !== "documentActionButtons" && key !== "documentEditorButtons"
        );
        entries = [...entries, ...docEntries];
      }
      return entries;
    }, [finalRow, baseColumns, filteredTableDef.id]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);

    const handleAction = useCallback(
      (action: string, opts: Record<string, any> = {}) => {
        if (action === "add") {
          if (hasOvertime) {
            setInfoOpen(true);
            return;
          }
          const safeRow = {
            ...row,
            date: row.date instanceof Date
              ? row.date
              : new Date(row.date),
          };
          handleMoreButton({ action, row: safeRow, ...opts });
          return;
        }
        if (
          action === "edit" &&
          filteredTableDef.id === "my-time-employee-admissions-table"
        ) {
          const safeRow = {
            ...row,
            date: row.date instanceof Date ? row.date : new Date(row.date),
          };
          handleMoreButton({ action, row: safeRow, ...opts });
          return;
        }
        if (action === "edit") {
          handleMoreButton({ action, row, item: row });
          return;
        }
        if (action === "copy") {
          handleMoreButton({ action, row, item: row });
          return;
        }
        if (action === "accepted" || action === "rejected") {
          handleMoreButton({ action, row, id: row._id });
        } else {
          handleMoreButton({ action, row, ...opts });
        }
      },
      [filteredTableDef.id, hasOvertime, row, handleMoreButton],
    );

    const renderAcknowledgement = (): ReactNode => {
      const isAcknowledged = !!row.acknowledgedAt;
      return (
        <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
          <InlineIcon
            width="24px"
            height="24px"
            icon={isAcknowledged ? TrueIcon : FalseIcon}
            color="#919699"
          />
        </Box>
      );
    };

    const renderApprovement = (): ReactNode => {
      const isApproved = !!row.approvedAt;
      const dialogTitle = isApproved
        ? t("MY_TIME_UNLOCK_CLOSE_MONTH_TITLE")
        : t("MY_TIME_CONFIRM_CLOSE_MONTH_TITLE");
      const dialogTexts = isApproved
        ? [t("MY_TIME_UNLOCK_CLOSE_MONTH_INFO")]
        : [
          t("MY_TIME_CONFIRM_GROUP_CLOSE_MONTH_TEXT"),
          t("MY_TIME_CONFIRM_GROUP_CLOSE_MONTH_TEXT_RESPONSIBILITY"),
        ];
      const buttonText = isApproved
        ? t("MY_HR_UNLOCK")
        : t("MY_TIME_CLOSE_MONTH");

      const handleConfirm = () => {
        setApproveDialogOpen(false);
        handleMoreButton({ action: isApproved ? "open" : "close", row });
      };

      const handleCancel = () => {
        setApproveDialogOpen(false);
      };


      return (
        <>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            onClick={() => setApproveDialogOpen(true)}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              width={36}
              height={36}
              sx={{
                borderRadius: "50%",
                border: `1px solid ${isApproved ? "#2FA84F" : "#cc0033"}`,
              }}
            >
              <InlineIcon
                width="24px"
                height="24px"
                icon={isApproved ? LockIcon : UnlockIcon}
                color={isApproved ? "#2FA84F" : "#cc0033"}
              />
            </Box>
          </Box>
          <OcConfirmationDialog
            showDialog={approveDialogOpen}
            onClose={handleConfirm}
            onCancel={handleCancel}
            title={dialogTitle}
            texts={dialogTexts}
            okButtonText={buttonText}
            cancelButtonText={t("CANCEL")}
          />
        </>
      );
    };

    const renderAuthorization = (): ReactNode => {
      const isAuthorized = !!row.authorizedAt;
      return (
        <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
          <InlineIcon
            width="24px"
            height="24px"
            icon={isAuthorized ? TrueIcon : FalseIcon}
            color="#919699"
          />
        </Box>
      );
    };

    const renderViewWorkSchedule = (): ReactNode => {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          onClick={() => handleMoreButton({ action: "view", row })}
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={36}
            height={36}
            sx={{
              borderRadius: "50%",
              border: "1px solid #b2b4b6",
              cursor: "pointer",
            }}
          >
            <InlineIcon
              width="24px"
              height="24px"
              icon="mdi:eye"
              color={theme.palette.gray80.main}
            />
          </Box>
        </Box>
      );
    };

    const renderers: Record<string, (value?: any) => ReactNode> = {
      summaryCost: v => <Box>{`${v.toLocaleString("hu-HU")} Ft`}</Box>,
      documentEditorButtons: () => (
        <>
          <OcConfirmationDialog
            showDialog={confirmOpen}
            onClose={() => removeUpload(row)}
            onCancel={() => setConfirmOpen(false)}
            title={t("OC_DELETE_CONFIRM")}
            texts={[t("MY_DOCUMENTS_DELETE_CONFIRM_INFO")]}
            okButtonText={t("OC_ALL_RIGHT")}
            cancelButtonText={t("OC_CANCEL")}
          />
          <MyDocumentsDocumentEditorButtons
            setShowConfirmDialog={setConfirmOpen}
            onEditDocument={onEditDocument}
            row={row}
          />
        </>
      ),
      documentActionButtons: () => (
        <Box display="flex">
          <MyDocumentsActionCell
            document={{
              ...row,
              filename: row.filename || "",
              slug: row.slug || "",
            }}
            isMobile={isMobile}
          />
        </Box>
      ),
      icons: () => (
        <Box display="flex">
          {row.icons?.add && !isNotMyTimeExaminer && (
            <Box
              onClick={() => handleAction("add")}
              sx={{
                ...ICON_BUTTON_STYLE,
                mr: row.icons.arrow || row.icons.edit ? 1 : 0,
              }}
            >
              <InlineIcon icon={plusIcon} width={24} height={24} />
            </Box>
          )}
          {row.icons?.arrow && !isNotMyTimeExaminer && (
            <Box onClick={() => handleAction("show")} sx={ICON_BUTTON_STYLE}>
              <InlineIcon icon={arrowIcon} width={24} height={24} />
            </Box>
          )}
          {row.icons?.edit && !isNotMyTimeExaminer && (
            <Box onClick={() => handleAction("edit")} sx={ICON_BUTTON_STYLE}>
              <InlineIcon icon={pencilIcon} width={24} height={24} />
            </Box>
          )}
        </Box>
      ),
      editButton: () => (
        <Box display="flex" justifyContent="flex-end">
          <Box
            onClick={() => handleAction("edit")}
            sx={{
              border: `1px solid ${_inlineEdit ? "#919699" : "#C6CACC"}`,
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: _inlineEdit ? "pointer" : "default",
              color: _inlineEdit
                ? theme.palette.gray80.main
                : theme.palette.gray40.main,
              pointerEvents: _inlineEdit ? "auto" : "none",
            }}
          >
            <InlineIcon icon={pencilIcon} width={18} height={18} />
          </Box>
        </Box>
      ),
      myTimeActions: () => (
        <Box display="flex" justifyContent="flex-end">
          <Box
            onClick={() => handleMoreButton(row)}
            sx={{
              border: `1px solid ${theme.palette.gray60.main}`,
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: theme.palette.gray60.main,
              pointerEvents: "auto",
            }}
          >
            <InlineIcon icon={pencilIcon} width={18} height={18} />
          </Box>
        </Box>
      ),
      copyButton: () => (
        <Button
          variant="contained"
          color="primary"
          disableElevation
          disabled={!row.canCopy}
          onClick={() => handleAction("copy")}
        >
          {t("MY_CAFETERIA_COPY").toString()}
        </Button>
      ),
      actionButtons: () => (
        <Box display="flex">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={36}
            height={36}
            sx={{
              backgroundColor: "#2FA84F",
              borderRadius: 1,
              cursor: "pointer",
            }}
            onClick={() => handleAction("accepted")}
          >
            <InlineIcon color="white" icon={checkBold} width={20} height={20} />
          </Box>
          <Box width={4} />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={36}
            height={36}
            sx={{
              backgroundColor: "#cc0033",
              borderRadius: 1,
              cursor: "pointer",
            }}
            onClick={() => handleAction("rejected")}
          >
            <InlineIcon color="white" icon={closeThick} width={20} height={20} />
          </Box>
        </Box>
      ),
      date: () => {
        if (!row.startTime) return <Box />;
        const fmt = (s: string) => moment(s).format("L HH:mm");
        return (
          <Box>
            {row.startTime && row.endTime
              ? `${fmt(row.startTime)} - ${fmt(row.endTime)}`
              : ""}
          </Box>
        );
      },
      startDate: v => (
        <Box>{`${new Date(v as string).toLocaleDateString()} - ${new Date(
          row.endDate as string
        ).toLocaleDateString()}`}</Box>
      ),
      description: () => (
        <Box>
          {row.description?.includes(" ")
            ? row.description
            : getFormatTextWidthValue(row.description!, 15)}
        </Box>
      ),
      filename: v => <Box>{getFormatTextWidthValue(v as string, 20)}</Box>,
      email: v => <Box>{getFormatTextWidthValue(v as string, 15)}</Box>,
      userView: () =>
        !myDocumentsOperator ? (
          <MyDocumentsDocumentVisibility visibility={row.userView} />
        ) : null,
      user: v => <Box>{parsePipedName(v as string, i18n)}</Box>,
      size: () => <Box>{getFormatBytes(row.size!)}</Box>,
      typeName: () => <Box>{t(`MY_LEAVE_${row.typeName}`).toString()}</Box>,
      createdAt: v => {
        if (v == null || v === "") {
          return <Box />;
        }
        const isTax =
          filteredTableDef.id === "my-hr-tax-credit-application-requests-table";
        const toDate = (value: any) =>
          value instanceof Date ? value : new Date(value);
        const date = toDate(v);
        if (Number.isNaN(date.getTime())) {
          return <Box>{String(v)}</Box>;
        }
        const dateFn = isTax
          ? (d: Date) => d.toLocaleDateString()
          : (d: Date) =>
            d
              .toLocaleString("hu-HU")
              .replace(/,/g, "")
              .replace(/\//g, ".");
        return <Box>{dateFn(date)}</Box>;
      },
      state: v =>
        filteredTableDef.id !== "my-hr-tax-credit-application-requests-table" ? (
          <OcDataTableBodyCellState label={t(v as string)} />
        ) : (
          <ApplicationState label={t(v as string)} />
        ),
      type: () => (
        <Box>
          {filteredTableDef.id !== "my-hr-tax-credit-application-requests-table"
            ? (t(`MY_LEAVE_${row.type}`) as string)
            : (t(row.type!) as string)}
        </Box>
      ),
      error: () => <Box>{t(row.error!).toString()}</Box>,
      role: () => (
        <Box>
          {t(`MY_HR_ROLE_${row.role!.toUpperCase()}`).toString()}
        </Box>
      ),
      active: () => <Checkbox checked={!!row.active} size="small" sx={{ p: 0.5 }} />,
      acknowledgedAt: () => renderAcknowledgement(),
      myTimeType: () => <Box>
        {row.myTimeType
          ? t(`MY_TIME_${row.myTimeType}`).toString()
          : ""
        }
      </Box>,
      messages: () => {
        const hookCount = hasIndicator ? (messages?.length ?? 0) : (finalRow.messages?.length ?? 0);
        const countFromRow = row.messageCount ?? 0;
        const hasAnyMessages = hookCount > 0 || countFromRow > 0;

        return (
          <InlineIcon
            icon={hasAnyMessages ? messageText : messageOutline}
            width={20}
            height={20}
          />
        );
      },
    };

    CURRENCY_KEYS.forEach(key => {
      renderers[key] = (v: any) => <Box>{v != null ? `${v.toLocaleString("hu-HU")} Ft` : ""}</Box>;
    });

    if (filteredTableDef.id === "my-sheet-costs-settings-table-def") {
      renderers.value = (v: any) => (
        <Box>{v != null ? `${Number(v).toLocaleString("hu-HU")} Ft` : ""}</Box>
      );
    }

    // Add renderers for my-time dashboard tables
    const dashboardTableIds = [
      "my-time-dashboard-list-table",
      "my-time-dashboard-list-table-authorization",
      "my-time-dashboard-list-table-examiner-approval-only",
      "my-time-dashboard-list-table-viewer",
    ];

    if (dashboardTableIds.includes(filteredTableDef.id)) {
      renderers["approvedAt"] = () => renderApprovement();
      renderers["authorizedAt"] = () => renderAuthorization();
      renderers["viewWorkSchedule"] = () => renderViewWorkSchedule();
    }
    return (
      <Box p="0 8px" display="flex" flexDirection="column">
        {rowEntries.map(([key, value], idx) => {
          const col = columns.find(c => c.id === key);
          const hideLabel = (HIDE_LABEL_KEYS as readonly string[]).includes(key as any);

          const defaultKey = key.replaceAll(/([A-Z])/g, " $1").toUpperCase();
          const rawLabelKey = LABEL_OVERRIDES[key] ?? col?.label ?? defaultKey;
          const displayKey = hideLabel ? "" : t(rawLabelKey);

          const rendered = renderers[key]?.(value) ?? (value as ReactNode);
          return (
            <Box
              key={idx}
              display="flex"
              flexDirection="column"
              borderBottom={`1px solid ${theme.palette.divider}`}
            >
              <Box display="flex" alignItems="center" p="8px 0">
                <Typography
                  variant="body2"
                  sx={{ width: "50%", color: theme.palette.gray80.main }}
                >
                  {displayKey}
                </Typography>
                <Box
                  sx={{
                    width: "50%",
                    display: "flex",
                    justifyContent: CENTER_KEYS.includes(key as any)
                      ? "center"
                      : "flex-start",
                  }}
                >
                  <Typography
                    variant="body2"
                    color={theme.palette.gray80.main}
                    component="span"
                  >
                    {rendered}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
        {!NO_MORE_BUTTON_TABLES.includes(filteredTableDef.id) && (
          <Box mt={2}>
            <Button
              variant="contained"
              fullWidth
              color="primary"
              onClick={() => handleMoreButton({ action: "defaultAction", row })}
            >
              {t("OC_MORE").toString()}
            </Button>
          </Box>
        )}
        <Box height={8} />
        <OcInfoDialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          handleDialogClose={() => setInfoOpen(false)}
          text={t("MY_TIME_ONE_OVERTIME_ALLOWED_TEXT")}
          isMobile={isMobile}
        />
      </Box>
    );
  }
);

export default OcIsMobileTableCollapse;
